/**
 * Fill a curated layout's slots with the live tapestry's symbols and phrases —
 * the frontend analogue of platform's domain/algo/tapgen/process_slots.py.
 *
 * It is a *spiritual* port, not a 1:1 one: the live state has only what the
 * reducer tracks (symbol_id, count, role, and a buffer of scored sentences)
 * plus master-symbol `tags`. It has none of the server-side deriv richness
 * (part-of-speech, symbol links, keyPhrases, emphasis, rulepaths). So:
 *   - `symbols` and `tags` rules are honored (tags via the master library);
 *   - `pos` rules degrade to the count-ordered fallback (no POS frontend-side);
 *   - `scope`/`min_words`/`max_words`/`fallback_phrase` don't occur in the data
 *     and are not implemented.
 *
 * The layout's main textpaths only ever use WHOLE sentences provided by the
 * backend — never frontend-sliced clauses (those are reserved for the smaller
 * textpaths inside symbol art, handled in TapestrySymbol). If distinct sentences
 * run out, a textpath is left empty rather than filled with a fragment.
 *
 * Low-capacity textpaths (a single short line) are the exception: a whole
 * sentence would have to shrink/compress to fit, so a symbol slot shows a single
 * library word (lemma) for its symbol instead, and a non-symbol low-capacity
 * slot is left empty. Multi-line textpaths still use whole sentences.
 *
 * ---------------------------------------------------------------------------
 * Recency balance (TT-973)
 * ---------------------------------------------------------------------------
 * Text used to be assigned by a single greedy pass over one ranked candidate
 * list, with no memory of the previous pass. Two things followed from that:
 *
 *   - the sentences frozen as "on screen at commit" were hard-partitioned to the
 *     HEAD of that list for the rest of the meeting, so whichever slots were
 *     filled first held that early text indefinitely;
 *   - the slots left over drew from the recency-decayed tail, which re-ranks on
 *     every sentence (and churns as the reducer evicts buffered sentences), so
 *     those few swapped text on nearly every tick.
 *
 * The scene read as stark: a couple of slots flickering beside a majority that
 * never moved. Now the fill is *stateful across passes* and paced:
 *
 *   1. Each sentence slot has a TEMPERAMENT (see TIERS below) — `fresh`, `mid`,
 *      or `anchor` — handed out by position so they interleave. It decides both
 *      how the slot ranks candidates (recency-hungry → recency-neutral) and how
 *      long it holds one.
 *   2. A slot RETAINS its previous sentence by default. It may only be replaced
 *      once it has served its temperament's `dwellMin`, and is forced to rotate
 *      at `dwellMax` — so nothing settles permanently and nothing thrashes.
 *   3. Optional changes (an upgrade, or a forced rotation) are rate-limited
 *      scene-wide by CHANGE_COOLDOWN + MAX_CHANGES_PER_FILL, and the scene spends
 *      that budget on the slot under the most pressure. Turnover therefore
 *      ROTATES through every slot at a readable pace instead of concentrating in
 *      two. Filling an EMPTY slot is never rate-limited — a blank guide is worse.
 *   4. The on-screen-at-commit set is a bonus DECAYING over ONSCREEN_GRACE
 *      sentences rather than a permanent partition: it still smooths the
 *      generative→curated handoff, then stops mattering.
 *
 * Prior assignments arrive via `opts.previous` and the next set is returned as
 * `assignments`; the caller owns that value between passes and hands it back.
 * Assignments recorded after the current playhead are dropped, so scrubbing
 * backward forgets text placed later (the playhead-derived model TT-961's
 * seen-reveal registry uses). Note the weaker guarantee, though: that registry
 * is monotone and replays exactly, whereas dwell and the change budget depend on
 * the *sequence* of fills — scrubbing back and forward yields a valid scene, not
 * necessarily the identical one.
 *
 * Pure function — no DOM, no I/O. Geometry (x/y/maxSize/textpath/textpathData)
 * is passed through untouched; mapping the layout's pixel-space `textpath` into
 * canvas units is a rendering concern handled downstream.
 */

import { decayedScore, RECENCY_HALFLIFE, RECENCY_FLOOR } from './recency';

// ---------------------------------------------------------------------------
// Turnover policy — every knob governing "how fast does the text change" lives
// here, so the scene can be re-tuned from one place.
// ---------------------------------------------------------------------------

/**
 * Slot temperaments. Every sentence slot in a curated layout gets one, and it
 * decides two things about that slot:
 *
 *   - how it RANKS candidate sentences (`halfLife`/`floor`): a `fresh` slot
 *     strongly favors what was just said; an `anchor` slot is recency-neutral and
 *     holds the meeting's best line; `mid` sits between them.
 *   - how long it HOLDS one (`dwellMin`/`dwellMax`, in sentences): a slot may not
 *     give up its sentence before `dwellMin`, and is forced to rotate once it has
 *     held the same one for `dwellMax`.
 *
 * Mixing temperaments across a scene is the actual rebalance: instead of a couple
 * of slots churning on the recency-decayed tail while the rest sit frozen on the
 * generative-phase text, every slot turns over — some quickly, some slowly, none
 * never.
 *
 * For scale: a meeting sentence is roughly 5–10s of speech, so a 30–60 minute
 * meeting runs ~200–600 sentences. Every tier's `dwellMax` is deliberately inside
 * that range — an anchor should turn over once or twice in a normal meeting, not
 * sit untouched to the end.
 */
const TIERS = {
  fresh: { halfLife: 25, floor: 0.1, dwellMin: 10, dwellMax: 45 },
  mid: { halfLife: RECENCY_HALFLIFE, floor: RECENCY_FLOOR, dwellMin: 30, dwellMax: 120 },
  // floor 1 ⇒ no decay at all: ranked on raw salience.
  anchor: { halfLife: RECENCY_HALFLIFE, floor: 1, dwellMin: 60, dwellMax: 180 },
};

// Temperaments are handed out by position so they interleave across the scene
// (adjacent slots rarely share one) and stay stable for a given layout — the same
// slot keeps the same temperament on every fill, which is what makes dwell mean
// anything.
const TIER_CYCLE = ['fresh', 'mid', 'anchor'];

function tierFor(ordinal, isKey) {
  // The key sentence is the scene's headline quote — always an anchor.
  return TIERS[isKey ? 'anchor' : TIER_CYCLE[ordinal % TIER_CYCLE.length]];
}

// A challenger must beat the incumbent's rank by this much to take over a slot
// that has served its minimum dwell. Keeps near-ties from swapping text.
const UPGRADE_MARGIN = 0.25;
// Scene-wide pace for *optional* changes (upgrades + forced rotations): one per
// this many sentences, however many slots are eligible at once.
//
// The allowance is spent in SENTENCES, not per fill, because how often the fill
// runs is up to the host: the flat stream (TT-955) delivers one sentence per
// segment, but a batched/checkpoint payload can deliver twenty. Budgeting per
// fill would make the scene's pace an accident of the host's batch size — dozens
// of sentences of new content would buy exactly one change.
const CHANGE_COOLDOWN = 4;
// ...capped so a big batch (or a long scrub jump) catches up gradually instead of
// swapping a third of the scene's text in one frame.
const MAX_CHANGES_PER_FILL = 3;
// Rank bonus for a sentence that was still on screen when the curated layout
// committed: it doubles that sentence's rank at the moment of commit, decaying
// linearly to nothing over ONSCREEN_GRACE sentences. Long enough to carry the
// generative→curated handoff (text moves into slots rather than being replaced),
// short enough that the early text isn't privileged all meeting.
const ONSCREEN_BONUS = 1;
const ONSCREEN_GRACE = 30;

function coerceNum(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

// Substring-overlap reuse test, mirroring process_slots.phrase_not_used: a
// candidate is "used" if it equals, contains, or is contained by an already
// placed phrase. Keeps near-duplicate text off two slots. Exported so the
// renderer (CuratedText) can apply the same test when it swaps in a fit fallback.
export function phraseNotUsed(phrase, used) {
  for (const p of used) {
    if (phrase === p || phrase.includes(p) || p.includes(phrase)) return false;
  }
  return true;
}

// Identity of a sentence for dedup purposes. Matches normalizeSeenKey in
// seenReveal.js — the identity CuratedText renders blocks under — so two
// sentences that the renderer treats as one can't both become candidates. (Not
// imported from there: that module is a 'use client' React context and this one
// is deliberately pure.)
function textKey(text) {
  return (text || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function wordCount(s) {
  return s.trim().split(/\s+/).filter(Boolean).length;
}

function inWindow(text, minWords = 1, maxWords = null) {
  const wc = wordCount(text);
  if (wc < minWords) return false;
  if (maxWords && wc > maxWords) return false;
  return true;
}

// Best-ranked candidates that fit the word window and aren't already placed, in
// rank order. One predicate for every place that consumes candidates — the
// retain check, the two pick sites, and the fallback list — so they can't drift.
function pickCandidates(cands, used, minWords, maxWords, { exclude = null, limit = 1 } = {}) {
  const out = [];
  for (const c of cands) {
    if (out.length >= limit) break;
    if (!c || !c.text || c.text === exclude) continue;
    if (!inWindow(c.text, minWords, maxWords)) continue;
    if (phraseNotUsed(c.text, used)) out.push(c);
  }
  return out;
}

function pickCandidate(cands, used, minWords, maxWords, exclude = null) {
  return pickCandidates(cands, used, minWords, maxWords, { exclude })[0] || null;
}

// How a slot of a given temperament ranks one buffered sentence: recency-weighted
// salience, plus the on-screen-at-commit bonus decaying over ONSCREEN_GRACE.
function rankOf(sent, ctx, tier) {
  const base = decayedScore(sent, ctx.now, tier.halfLife, tier.floor);
  if (!ctx.onscreen || ctx.onscreenAge >= ONSCREEN_GRACE) return base;
  const weight = 1 - ctx.onscreenAge / ONSCREEN_GRACE;
  return ctx.onscreen.has(sent.text) ? base * (1 + ONSCREEN_BONUS * weight) : base;
}

// Distinct sentences ranked best-first for one temperament. Each candidate keeps
// its dedup key so downstream merges don't recompute it.
function rankSentences(sentences, ctx, tier) {
  const seen = new Set();
  const out = [];
  for (const s of sentences || []) {
    if (!s || !s.text) continue;
    const key = textKey(s.text);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ text: s.text, key, rank: rankOf(s, ctx, tier) });
  }
  out.sort((a, b) => b.rank - a.rank);
  return out;
}

// A symbol slot prefers its own symbol's sentences (topical), then falls back to
// the whole tapestry's supply so a thin per-symbol buffer can't strand the slot.
function candidatesFor(ownSentences, globalRanked, ctx, tier) {
  if (!ownSentences || !ownSentences.length) return globalRanked;
  const own = rankSentences(ownSentences, ctx, tier);
  const have = new Set(own.map((c) => c.key));
  return [...own, ...globalRanked.filter((c) => !have.has(c.key))];
}

// Ordered single-word library entries (lemmas) for a symbol — its name's words first,
// then tags/emotions (Set insertion order). Empty if the symbol is unknown.
function lemmasOf(symbolId, symbolKeywords) {
  if (!symbolId || !symbolKeywords) return [];
  const kw = symbolKeywords.get(symbolId);
  return kw ? [...kw] : [];
}

// Every sentence across the whole tapestry — the universal supply any layout
// textpath can fall back to so a thin/already-used per-symbol buffer doesn't
// strand a slot.
function flattenSentences(symbolsMap) {
  const all = [];
  for (const symbolId in symbolsMap) {
    for (const s of symbolsMap[symbolId].sentences || []) all.push(s);
  }
  return all;
}

// How many lines a slot's textpath holds: an aggregate array is one line per
// guide; a single-string textpath uses textpathData.lines (default 1). Slots
// with a single line are "low capacity" → prefer short clauses over sentences.
function capacityOf(slot, td) {
  if (Array.isArray(slot.textpath)) return slot.textpath.length;
  return Number(td && td.lines) || 1;
}

function rankedIds(symbolsMap) {
  return Object.entries(symbolsMap)
    .sort((a, b) => {
      if (b[1].count !== a[1].count) return b[1].count - a[1].count;
      return (a[1].firstIdx ?? 0) - (b[1].firstIdx ?? 0);
    })
    .map(([symbolId]) => symbolId);
}

function ruleOf(slot, type) {
  return (slot.rules || []).find((r) => r && r.type === type) || null;
}

/**
 * Read the caller's assignment memory. Two things are dropped:
 *   - everything, if it was recorded under a different layout (a background
 *     switch starts a fresh scene; backgrounds SHARING a layout keep their dwell,
 *     so text that persists across such a switch doesn't restart its clock);
 *   - anything recorded after the current playhead, so scrubbing backward forgets
 *     text placed later.
 */
function readMemory(previous, now, layoutKey) {
  const slots = new Map();
  let lastChangeAt = null;
  if (previous && previous.layoutKey === layoutKey) {
    for (const [key, entry] of previous.slots) {
      if (entry && entry.phrase && entry.placedAt <= now) slots.set(key, entry);
    }
    if (previous.lastChangeAt != null && previous.lastChangeAt <= now) lastChangeAt = previous.lastChangeAt;
  }
  return { slots, lastChangeAt };
}

// Choose a symbol for a symbol slot following the rule cascade, skipping any
// already used. `pool` is the count-ordered list of live symbol_ids.
function chooseSymbol(slot, pool, used, { symbolsMap, symbolTags, primary }) {
  const available = (symbolId) => !used.has(symbolId);

  // primary slot prefers the committed central symbol
  if (slot.primary && primary && available(primary)) return primary;

  const symbolsRule = ruleOf(slot, 'symbols');
  if (symbolsRule && Array.isArray(symbolsRule.symbol_ids)) {
    const hit = pool.find((id) => symbolsRule.symbol_ids.includes(id) && available(id));
    if (hit) return hit;
  }

  const tagsRule = ruleOf(slot, 'tags');
  if (tagsRule && Array.isArray(tagsRule.tags) && symbolTags) {
    const wanted = new Set(tagsRule.tags);
    let best = null;
    let bestN = 0;
    for (const id of pool) {
      if (!available(id)) continue;
      const tags = symbolTags.get(id) || [];
      const overlap = tags.reduce((acc, t) => acc + (wanted.has(t) ? 1 : 0), 0);
      if (overlap > bestN) {
        bestN = overlap;
        best = id;
      }
    }
    if (best) return best;
  }

  // `pos` rules (and any unmatched cascade) fall through to next-by-count.
  return pool.find(available) || null;
}

// How many fallback phrases to hand the renderer per slot (see the fit-fallback
// note where they're attached).
const FALLBACK_LIMIT = 6;

/**
 * @param {object} layout       background-detail layout: { id, name, slots, title }
 * @param {object} state        reducer state: { symbols, primary, ranked, playheadSent }
 * @param {number} playheadSent the sentence clock dwell and recency are measured in
 * @param {object} [opts]
 * @param {Map<string,string[]>}    [opts.symbolTags]        symbol_id -> tags (master lib)
 * @param {Map<string,Set<string>>} [opts.symbolKeywords]    symbol_id -> library words (lemmas)
 * @param {Set<string>}             [opts.onscreenSentences] sentence text shown at commit
 * @param {number}                  [opts.onscreenCommitSent] sentence index of that commit
 * @param {object}                  [opts.previous]          `assignments` from the last fill
 * @returns {{ slots: Array, title: object|null, assignments: object }}
 */
export function fillSlots(layout, state, playheadSent, opts = {}) {
  const symbolsMap = (state && state.symbols) || {};
  const {
    symbolTags = null,
    onscreenSentences = null,
    onscreenCommitSent = null,
    symbolKeywords = null,
    previous = null,
  } = opts;
  const primary = state && state.primary;
  const now = playheadSent;

  const rawSlots = (layout && layout.slots) || [];
  const pool = rankedIds(symbolsMap);
  const allSentences = flattenSentences(symbolsMap);

  const ctx = {
    now,
    onscreen: onscreenCommitSent == null ? null : onscreenSentences,
    onscreenAge: onscreenCommitSent == null ? Infinity : now - onscreenCommitSent,
  };
  // Ranked global supply per temperament, built on demand (each is a different
  // ordering of the same sentences).
  const globalByTier = new Map();
  const globalFor = (tier) => {
    let ranked = globalByTier.get(tier);
    if (!ranked) {
      ranked = rankSentences(allSentences, ctx, tier);
      globalByTier.set(tier, ranked);
    }
    return ranked;
  };

  // Layout the memory was recorded under (see readMemory).
  const layoutKey = (layout && (layout.id ?? layout.name)) || 'layout';
  const memory = readMemory(previous, now, layoutKey);

  const usedSymbols = new Set();
  const usedPhrases = [];
  const usedLemmas = new Set();
  const filledByIdx = new Map();
  // Sentence slots, in fill order, each awaiting a retain/refresh decision. A
  // plan keyed by slot POSITION rather than `slot.id`, because layout data does
  // contain duplicate slot ids (see uniqueTextId in curatedLayout.js).
  const plans = [];
  let sentenceOrdinal = 0;
  const addPlan = (idx, isKey, ownSentences, minW, maxW) => {
    const tier = tierFor(sentenceOrdinal++, isKey);
    plans.push({
      idx,
      tier,
      cands: candidatesFor(ownSentences, globalFor(tier), ctx, tier),
      minW,
      maxW,
    });
  };

  // Fill phrase slots before symbol slots so key sentences are reserved first
  // (process_slots does the same via reorder_slots("phrase")). Output order is
  // restored to the layout's original order afterward.
  const order = rawSlots
    .map((slot, idx) => ({ slot, idx }))
    .sort((a, b) => {
      const pa = a.slot.type === 'phrase' ? 0 : 1;
      const pb = b.slot.type === 'phrase' ? 0 : 1;
      return pa - pb || a.idx - b.idx;
    });

  // ---- Pass 1: geometry + symbols; sentence slots become plans ----
  for (const { slot, idx } of order) {
    const base = slotBase(slot, idx);

    if (slot.type === 'symbol') {
      const symbolId = chooseSymbol(slot, pool, usedSymbols, { symbolsMap, symbolTags, primary });
      if (symbolId) usedSymbols.add(symbolId);
      const stylesRule = ruleOf(slot, 'symbol_styles');
      filledByIdx.set(idx, {
        ...base,
        primary: slot.primary ?? false,
        symbolId,
        symbolStyles: stylesRule ? stylesRule.styles : null,
        phrase: null,
        isKeySentence: false,
      });

      if (base.textpath || slot.phrase_space != null) {
        const td = base.textpathData || {};
        if (capacityOf(slot, td) <= 1) {
          // Low-capacity: a single library word (lemma) for this symbol. A whole
          // sentence would compress to fit and frontend-sliced clauses read as
          // nonsense, so use the symbol's own library entries — else leave empty.
          const lemma = lemmasOf(symbolId, symbolKeywords).find((w) => !usedLemmas.has(w)) || null;
          if (lemma) usedLemmas.add(lemma);
          filledByIdx.get(idx).phrase = lemma;
        } else {
          // Multi-line path: whole backend sentences (own first, then global);
          // left empty rather than sliced if none remain.
          addPlan(idx, false, symbolId ? symbolsMap[symbolId].sentences : null, td.min_words || 1, td.max_words || null);
        }
      }
    } else {
      // phrase / key-sentence slot. Key sentences and multi-line phrase slots use
      // whole backend sentences. A low-capacity, non-key phrase slot has no symbol
      // to draw a lemma from, so it stays empty rather than showing a clause.
      const isKey = !!ruleOf(slot, 'key_sentence');
      filledByIdx.set(idx, { ...base, phrase: null, isKeySentence: isKey });
      if (isKey || capacityOf(slot, base.textpathData || {}) > 1) addPlan(idx, isKey, null, 1, null);
    }
  }

  // Claim a sentence for a slot. Every phrase placed this pass goes into
  // `usedPhrases` so no two slots can show overlapping text.
  const claim = (plan, phrase, placedAt) => {
    plan.phrase = phrase;
    plan.placedAt = placedAt;
    usedPhrases.push(phrase);
  };

  // ---- Pass 2: retain what each slot already holds ----
  // Retention is a DISPLAY fact, deliberately independent of whether the sentence
  // is still in the reducer's buffer: buffer eviction was a large share of the old
  // churn, and dwellMax still guarantees the slot rotates.
  for (const plan of plans) {
    const held = memory.slots.get(plan.idx);
    if (!held || !inWindow(held.phrase, plan.minW, plan.maxW) || !phraseNotUsed(held.phrase, usedPhrases)) continue;
    claim(plan, held.phrase, held.placedAt);
  }

  // ---- Pass 3: fill vacancies (never rate-limited) ----
  for (const plan of plans) {
    if (plan.phrase) continue;
    const hit = pickCandidate(plan.cands, usedPhrases, plan.minW, plan.maxW);
    if (hit) claim(plan, hit.text, now);
  }

  // ---- Pass 4: spend the change budget on the slots under the most pressure ----
  // A slot is eligible once it has served its dwellMin and a challenger clearly
  // out-ranks what it holds; it is FORCED once it has held the same sentence for
  // dwellMax. Forced rotations go first, then whichever slot is furthest past its
  // own dwell. A forced slot still needs a challenger — with a thin supply (every
  // other sentence already placed) it simply holds, which is the right answer.
  let lastChangeAt = memory.lastChangeAt;
  const budget =
    lastChangeAt == null
      ? 1 // nothing has changed yet — allow one, don't open with a burst
      : Math.min(MAX_CHANGES_PER_FILL, Math.floor((now - lastChangeAt) / CHANGE_COOLDOWN));
  if (budget > 0) {
    const eligible = [];
    for (const plan of plans) {
      if (!plan.phrase) continue;
      const heldFor = now - plan.placedAt;
      if (heldFor < plan.tier.dwellMin) continue;
      const challenger = pickCandidate(plan.cands, usedPhrases, plan.minW, plan.maxW, plan.phrase);
      if (!challenger) continue;
      const incumbent = plan.cands.find((c) => c.text === plan.phrase);
      const incumbentRank = incumbent ? incumbent.rank : 0; // evicted ⇒ no standing
      const forced = heldFor >= plan.tier.dwellMax;
      const gain = incumbentRank > 0 ? challenger.rank / incumbentRank - 1 : Infinity;
      if (!forced && !(gain > UPGRADE_MARGIN)) continue;
      // Pressure is measured against the slot's OWN dwell, not in absolute
      // sentences: a `fresh` slot at 3× its dwellMin outranks an `anchor` barely
      // past its own. That is what makes the scene-wide budget flow to slots in
      // proportion to their temperament instead of to whoever happens to have the
      // biggest score gain — the fast slots stay fast and the slow ones hold.
      eligible.push({ plan, challenger, forced, pressure: heldFor / plan.tier.dwellMin, heldFor });
    }
    eligible.sort((a, b) => {
      if (a.forced !== b.forced) return a.forced ? -1 : 1;
      if (a.pressure !== b.pressure) return b.pressure - a.pressure;
      return b.heldFor - a.heldFor;
    });
    for (const { plan, challenger } of eligible.slice(0, budget)) {
      // The outgoing phrase stays in `usedPhrases` so a freed sentence can't be
      // picked up by another slot in the same pass (no musical chairs).
      claim(plan, challenger.text, now);
      lastChangeAt = now;
    }
  }

  // ---- Commit the plans onto their slots ----
  // Each slot also gets ranked FALLBACK phrases, which the renderer swaps in when
  // the chosen phrase won't fit its guides (so it picks a phrase that fits rather
  // than shrinking/overrunning — see CuratedText). A fallback is any other
  // in-window candidate that doesn't overlap a phrase placed above, so the
  // renderer can never swap in a duplicate of another slot's text.
  const assignments = { layoutKey, lastChangeAt, slots: new Map() };
  for (const plan of plans) {
    if (!plan.phrase) continue;
    const entry = filledByIdx.get(plan.idx);
    entry.phrase = plan.phrase;
    assignments.slots.set(plan.idx, { phrase: plan.phrase, placedAt: plan.placedAt });

    const fallbacks = [];
    // Over-fetch, since the intra-list de-dup below rejects some of what comes back.
    for (const c of pickCandidates(plan.cands, usedPhrases, plan.minW, plan.maxW, {
      exclude: plan.phrase,
      limit: FALLBACK_LIMIT * 2,
    })) {
      if (fallbacks.length >= FALLBACK_LIMIT) break;
      if (fallbacks.some((f) => !phraseNotUsed(c.text, [f]))) continue; // de-dup within the list
      fallbacks.push(c.text);
    }
    if (fallbacks.length) entry.phraseFallbacks = fallbacks;
  }

  const slots = rawSlots.map((_, idx) => filledByIdx.get(idx)).filter(Boolean);

  return { slots, title: layoutTitle(layout), assignments };
}

// A layout slot's geometry and guides, coerced once. `idx` is its position
// in the layout (slot ids can repeat, see uniqueTextId in curatedLayout.js),
// which is how a recorded fill names a slot (replayFill).
function slotBase(slot, idx) {
  return {
    idx,
    id: slot.id,
    type: slot.type,
    x: coerceNum(slot.x),
    y: coerceNum(slot.y),
    maxSize: coerceNum(slot.max_size ?? slot.maxSize),
    textpath: slot.textpath || null,
    textpathData: slot.textpath_data ?? slot.textpathData ?? null,
  };
}

function layoutTitle(layout) {
  return layout && layout.title
    ? {
        x: coerceNum(layout.title.x),
        y: coerceNum(layout.title.y),
        maxSize: coerceNum(layout.title.max_size ?? layout.title.maxSize),
      }
    : null;
}

/**
 * A fill played back from a record of one (TT-1227 review cassette): the
 * layout's slots holding the symbol and phrase each held in a recorded
 * frame, in the shape `fillSlots` returns, so `buildCuratedComposition`
 * paints it the same way. `recorded` is `[{ i, symbolId?, phrase?,
 * fallbacks? }]`, `i` the slot's position in the layout; a slot the record
 * leaves out is empty. No scoring, no memory, no dwell: what was seen, not
 * what would be chosen now.
 */
export function replayFill(layout, recorded) {
  const rawSlots = (layout && layout.slots) || [];
  const byIdx = new Map();
  for (const r of recorded || []) if (Number.isInteger(r?.i)) byIdx.set(r.i, r);
  const slots = rawSlots.map((slot, idx) => {
    const base = slotBase(slot, idx);
    const r = byIdx.get(idx) || {};
    if (slot.type === 'symbol') {
      const stylesRule = ruleOf(slot, 'symbol_styles');
      return {
        ...base,
        primary: slot.primary ?? false,
        symbolId: r.symbolId || null,
        symbolStyles: stylesRule ? stylesRule.styles : null,
        phrase: r.phrase || null,
        phraseFallbacks: r.fallbacks || [],
        isKeySentence: false,
      };
    }
    return {
      ...base,
      phrase: r.phrase || null,
      phraseFallbacks: r.fallbacks || [],
      isKeySentence: !!ruleOf(slot, 'key_sentence'),
    };
  });
  return { slots, title: layoutTitle(layout), assignments: null };
}
